import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import BaseComponent from '../../baseClasses/BaseComponent';
import Download from '../Download';
import VideoPlayer from '../VideoPlayer';
import animator from '../../helpers/animator';
import AnalyticsLogger from '../../helpers/analytics/analyticsLogger';
import dataModel from '../../models/DataModel';
import FileUtil from '../../helpers/FileUtil/FileUtil';
import Metadata from '../../helpers/metadata/Metadata';
import AttachmentMetadata from '../../components/AttachmentMetadata';
import AttachmentHelper from '../../helpers/AttachmentHelper';
import DataSource from '../../dao/DataSource/DataSource';
import PhotoPermissionsUtil from '../../helpers/permissions/PhotoPermissionsUtil';

import './AttachmentPopup.scss';
import Input from '../Input';

/**
 * This component is used within <TaskCompletionStepPhotoGrid> for web (non-mobile)
 * uploads into the grid (when more than one photo exists under a completion step)
 */
export class AttachmentPopup extends BaseComponent {
  constructor(props) {
    super(props);

    AttachmentPopup.propTypes = {
      attachment: PropTypes.object.isRequired,
      handlePopupClose: PropTypes.func,
      hasMoreButton: PropTypes.bool,
    };

    this.state = { 
      // Possible values: 'none', 'menu', 'rename'
      editState: 'none',
      editName: FileUtil.trimExtension(props.attachment.name),
    };
    this.animated = false;
    this.animator = new animator();
    this.createResourceView = this.createResourceView.bind(this);
    const currentRole = props.currentUser ? props.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });

    this.renameAttachment = this.renameAttachment.bind(this);
  }

  handleAttachmentRetrieval(attachment, action, acData, fetchedData) {
    if (action == 'delete'){
      this.handleClose();
      this.analyticsLogger.logCSPhotoActivity('CompletionStepDeleted');
    }
    if (attachment){
      this.setState({ attachment });
      const extension = attachment.name.split('.')[1];
      const body = `${this.props.currentUser.name} downloaded ${attachment.name}`;
      Metadata.logEventHistory('Attachment Download', extension, body);
    }
  }

  componentDidMount() {

    if (this.props.attachment) {
      this.attachmentDataSource = new DataSource('Attachment', this.props.attachment.id, 
        { attachment: { conversions: {}, user: {}, additionalFields: ['conversions', 'upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at']  } },
        this.handleAttachmentRetrieval);
      this.attachmentDataSource.get();
    }

    this.handleClickOutside = event => {
      const { editState } = this.state;
      if (editState == 'none' && this.componentRef && this.saveRef && this.cancelRef &&
          !this.componentRef.contains(event.target) && !this.saveRef.contains(event.target) && !this.cancelRef.contains(event.target)) {
        this.handleClose();
      }
    };

    document.addEventListener('click', this.handleClickOutside, false);
  }

  componentWillUnmount() {
    if (this.attachmentDataSource){
      this.attachmentDataSource.release();
      this.attachmentDataSource = null;
    }
    document.removeEventListener('click', this.handleClickOutside, false);
  }

  animateMe() {
    this.animated = true;
    const _this = this;
    setTimeout(() => {
      const ani = _this.animator.createPropertyAnimation('view-attachment-popup' + this.state.attachment.id, 300, {
        opacity: { startVal: 0, endVal: 1 },
      });
      _this.animator.addAnimation(ani);
      _this.animator.processAnimationQueue();
    }, 10);
  }

  handleClose() {
    const ani = this.animator.createPropertyAnimation('view-attachment-popup' + this.state.attachment.id, 300, {
      opacity: { startVal: 1, endVal: 0 },
    });

    this.animator.addAnimation(ani);
    this.animator.processAnimationQueue(() => {
      const { handlePopupClose } = this.props;
      if (handlePopupClose) {
        handlePopupClose();
      }
    });
  }

  handleDeleteClicked() {

    dataModel.showGlobalModal('SYSTEM_POPUP', {
      message: 'Are you sure you want to delete "'+this.state.editName+'"? This action cannot be undone.',
      cancelButtonText: 'CANCEL',
      okButtonText: 'YES',
      width: 320,
      height: 182,
      handleOwnerOk: () =>  {
        this.attachmentDataSource.delete();
        this.handleClose();
      },
    });
  }

  handleRenameClicked(event) {
    event.nativeEvent.stopImmediatePropagation();
    this.setState({ editState: 'rename' });
  }

  handleNameChanged(event) {
    this.setState({ editName: event.target.value });
  }

  renameAttachment(e) {
    if(e) {
      e.preventDefault();
    }
    const { attachment } = this.state;
    const { editName } = this.state;
    const nameValid = FileUtil.nameIsValid(editName);
    if (nameValid) {
      const newName = this.state.editName + '.' + FileUtil.getExtension(attachment.name);
      if (newName != attachment.name) {
        const attachmentCopy = Object.assign({}, attachment);
        attachmentCopy.name = newName;
        dataModel.attachmentsDAO.updateAttachment(attachmentCopy, null, { recordDescriptor: { attachment: { user: {}, additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at']  } } }).then(attachment => {
          this.setState({ attachment, editName: FileUtil.trimExtension(attachment.name) });
        });
        this.analyticsLogger.logCSPhotoActivity('CompletionStepRenamed');
      }
      this.setState({ editState: 'none' });
    }
    else {
      dataModel.showGlobalModal('SYSTEM_POPUP', {
        message: 'Name cannot be empty and cannot contain any of the following characters:\n \\ / : ; {} [] <>  \' " ~ . , ! @ # $ % & *',
        oneButton: true,
        okButtonText: 'OK',
        width: 320,
        height: 182,
      });
    }
  }

  handleKeyDown(e) {
    if (e.keyCode == 27) {
      e.preventDefault();
      this.handleClose();
    }
  }

  createImageView(attachment) {
    let imageView = <img src={attachment.upload} alt='attachment' />;
    return imageView;
  }

  createVideoView(attachment){
    const type = attachment && attachment.upload_content_type ? attachment.upload_content_type : (attachment && attachment.upload && attachment.upload.type) ? attachment.upload.type : undefined;
    let videoView = <VideoPlayer src={attachment.upload} type={type}/>;
    return videoView;
  }

  createResourceView(attachment){
    let view = null;
    
    const extension = FileUtil.getExtension(attachment.name);
    switch(extension){
    case 'png':
    case 'jpg':
    case 'jpeg':
      view = this.createImageView(attachment);
      break;
    case 'mp4':
    case 'mov':
    case 'mp4v':
    case 'wmv':
    case 'flv':
    case 'qt':
    case 'm4v':
      view = this.createVideoView(attachment);
      break;
    case 'pdf':
    case 'doc':
    case 'docx':
    case 'xls':
    case 'xlt':
    case 'xlm':
    case 'xlsm':
    case 'xlsx':
    case 'xltx':
    default:
      view = AttachmentHelper.getConvertedDocumentView(attachment);
      break;
    }
    return view;
  }

  handleCancelRename(){
    this.setState({ editState: 'none', editName: FileUtil.trimExtension(this.state.attachment.name) });
  }
  
  render() {
    const { attachment, editState, editName } = this.state;
    const { task, completionStep, userStepPermissions } = this.props;
    let download;

    if (!attachment) {
      return null;
    }

    if (!this.animated) {
      this.animateMe();
    }

    const resource = this.createResourceView(attachment);

    if(this.props.download){
      let content = <div className='download'/>;
      download = <Download download={attachment.name} href={attachment.upload} content={content}/>;
    }
      
    let canDeleteAttachment = PhotoPermissionsUtil.canUserEditPhoto(userStepPermissions, completionStep, task);

    return (
      <div ref={el => this.componentRef = el} id={'attachment-popup' + (attachment.id ? attachment.id : Math.floor(Math.random() * 20000))} 
        className={'attachment-popup'} 
        onKeyDown={this.handleKeyDown}>
        <div className='attachment-content'>
          <div className='attachment-header'>
            <div className='attachment-buttons'>
              { canDeleteAttachment &&
                <div className="delete-button" onClick={this.handleDeleteClicked}/>
              }
              <div className="close-button" onClick={this.handleClose}/>
            </div>
            {editState == 'rename' ?
              <div className='attachment-caption' ref={el => this.titleRef = el}>
              <form onSubmit={this.renameAttachment} onBlur={this.renameAttachment}>
                <Input autoFocus className='attachment-title attachment-title--edit' value={editName} onChange={this.handleNameChanged} onFocus={e => e.target.select()} />
              </form> 
              {/* If we decide to do buttons <div className="save-button" onClick={this.renameAttachment} ref={el => this.saveRef = el} >Save</div>
              <div className="cancel-button" onClick={this.handleCancelRename} ref={el => this.cancelRef = el} /> */}
            </div>
            :
            <div className='attachment-caption' ref={el => this.titleRef = el}>
              <div className='attachment-title'  onClick={this.handleRenameClicked}>{editName}</div>
              {/* if we decice to do buttons <img className="pencil-icon" src={pencilIcon} alt="edit" onClick={this.handleRenameClicked}/> */}
            </div>}
            {download}
          </div>
          <div className='attachment-container'>
            <div className='resource-container'>
              <div className='attachment-resource'>
                {resource}
                <AttachmentMetadata attachment={attachment} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    currentUser: state.new_users ? state.new_users.currentUser : null,
  };
};
export default connect(mapStateToProps)(AttachmentPopup);



// WEBPACK FOOTER //
// ./src/components/AttachmentPopup/index.js